import { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { HEADER, NAVBAR } from '../../config';
import useCollapseDrawer from '../../hooks/useCollapseDrawer';
import useResponsive from '../../hooks/useResponsive';
import useSettings from '../../hooks/useSettings';
import { PATH_MAIN } from '../../routes/paths';
import MainHeader from './header';
import NavbarHorizontal from './navbar/NavbarHorizontal';
import NavbarVertical from './navbar/NavbarVertical';

type MainStyleProps = {
    collapseClick: boolean;
    isLiveSessionPage: boolean;
    isAppointmentsPage: boolean;
    isGroupSessionPage: boolean;
};

const MainStyle = styled('main', {
    shouldForwardProp: (prop) =>
        prop !== 'collapseClick' &&
        prop !== 'isLiveSessionPage' &&
        prop !== 'isAppointmentsPage' &&
        prop !== 'isGroupSessionPage',
})<MainStyleProps>(
    ({ collapseClick, isLiveSessionPage, isAppointmentsPage, isGroupSessionPage, theme }) => {
        const isFullBleedPage = isAppointmentsPage || isGroupSessionPage;

        return {
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            width: '100%',
            height: '100vh',
            boxSizing: 'border-box',
            [theme.breakpoints.down('lg')]: {
                paddingTop: HEADER.MOBILE_HEIGHT + 24,
            },
            [theme.breakpoints.up('lg')]: {
                position: 'relative',
                paddingLeft: isFullBleedPage ? 0 : 16,
                paddingRight: isFullBleedPage ? 0 : 16,
                paddingTop: isFullBleedPage
                    ? HEADER.MAIN_DESKTOP_HEIGHT
                    : HEADER.MAIN_DESKTOP_HEIGHT + 24,
                width: `calc(100% - ${NAVBAR.MAIN_WIDTH}px)`,
                transition: theme.transitions.create('margin-left', {
                    duration: theme.transitions.duration.shorter,
                }),
                ...(collapseClick && {
                    marginLeft: NAVBAR.MAIN_COLLAPSE_WIDTH,
                }),
            },
        };
    }
);

const MainLayout = () => {
    const [open, setOpen] = useState(false);

    const { collapseClick, isCollapse } = useCollapseDrawer();
    const { themeLayout } = useSettings();
    const { pathname } = useLocation();
    const isDesktop = useResponsive('up', 'lg');

    const verticalLayout = themeLayout === 'vertical';
    const isLiveSessionPage = pathname === PATH_MAIN.general.liveSession;
    const isAppointmentsPage = pathname === PATH_MAIN.general.appointments;
    const isGroupSessionPage = pathname === PATH_MAIN.general.groupSession;

    if (verticalLayout) {
        return (
            <>
                <MainHeader onOpenSidebar={() => setOpen(true)} verticalLayout={verticalLayout} />

                {isDesktop ? (
                    <NavbarHorizontal />
                ) : (
                    <NavbarVertical isOpenSidebar={open} onCloseSidebar={() => setOpen(false)} />
                )}

                <Box
                    className="main-layout"
                    component="main"
                    sx={{
                        display: 'flex',
                        px: { lg: 2 },
                        pt: {
                            xs: `${HEADER.MOBILE_HEIGHT + 24}px`,
                            lg: `${HEADER.MAIN_DESKTOP_HEIGHT + 80}px`,
                        },
                        pb: {
                            xs: `${HEADER.MOBILE_HEIGHT + 24}px`,
                            lg: `${HEADER.MAIN_DESKTOP_HEIGHT + 24}px`,
                        },
                        height: '100vh',
                        width: '100%',
                    }}
                >
                    <Outlet />
                </Box>
            </>
        );
    }

    return (
        <Box
            className="main-layout"
            component="main"
            sx={{
                display: 'flex',
                minHeight: { lg: 1 },
                height: '100vh',
            }}
        >
            <MainHeader isCollapse={isCollapse} onOpenSidebar={() => setOpen(true)} />

            <NavbarVertical isOpenSidebar={open} onCloseSidebar={() => setOpen(false)} />

            <MainStyle
                collapseClick={collapseClick}
                isLiveSessionPage={isLiveSessionPage}
                isAppointmentsPage={isAppointmentsPage}
                isGroupSessionPage={isGroupSessionPage}
            >
                <Outlet />
            </MainStyle>
        </Box>
    );
};

export default MainLayout;
